import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { endpoints } from '../api/endpoints.js'

/**
 * /health — unauthenticated frontend health page.
 *
 * Pings the backend liveness + readiness endpoints and shows the result.
 * Useful for:
 *   - Uptime monitors that need a single URL to stare at.
 *   - Manual sanity checks after a deploy.
 *   - Clinician-friendly "is the system up?" link.
 *
 * Intentionally bypasses auth and the route guards in App.jsx.
 */
export default function Health() {
  const [state, setState] = useState({ loading: true, health: null, ready: null, error: null })

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const out = { loading: false, health: null, ready: null, error: null }
      try {
        const h = await api.get(endpoints.health)
        out.health = { status: h.status, body: h.data }
      } catch (e) {
        out.health = { status: e.response?.status ?? 0, body: e.response?.data ?? String(e) }
      }
      try {
        const r = await api.get(endpoints.ready)
        out.ready = { status: r.status, body: r.data }
      } catch (e) {
        out.ready = { status: e.response?.status ?? 0, body: e.response?.data ?? String(e) }
      }
      if (!cancelled) setState(out)
    }
    run()
    const t = setInterval(run, 15_000)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  const statusDot = (probe) => {
    if (!probe) return 'bg-slate-300'
    if (probe.status === 200) return 'bg-emerald-500'
    return 'bg-rose-500'
  }

  const statusLabel = (probe) => {
    if (!probe) return 'pending'
    if (probe.status === 200) return 'ok'
    return `fail (${probe.status})`
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h1 className="text-lg font-semibold">Wantok Telehealth — system health</h1>
          <p className="text-xs text-slate-500 mt-1">
            Refreshes every 15 seconds. Build: {import.meta.env.VITE_APP_ENV || 'local'}.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className={`inline-block w-3 h-3 rounded-full ${statusDot(state.health)}`} />
          <div className="flex-1">
            <div className="text-sm font-medium">API liveness</div>
            <div className="text-[11px] text-slate-500 font-mono">GET {endpoints.health}</div>
          </div>
          <div className="text-xs font-medium">{state.loading ? '…' : statusLabel(state.health)}</div>
        </div>

        <div className="flex items-center gap-3">
          <span className={`inline-block w-3 h-3 rounded-full ${statusDot(state.ready)}`} />
          <div className="flex-1">
            <div className="text-sm font-medium">API readiness (DB + cache)</div>
            <div className="text-[11px] text-slate-500 font-mono">GET {endpoints.ready}</div>
          </div>
          <div className="text-xs font-medium">{state.loading ? '…' : statusLabel(state.ready)}</div>
        </div>

        {state.ready?.body && typeof state.ready.body === 'object' && state.ready.body.checks && (
          <div className="mt-2 rounded border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Readiness detail</div>
            <ul className="text-xs space-y-1">
              {Object.entries(state.ready.body.checks).map(([k, v]) => (
                <li key={k} className="flex justify-between gap-4">
                  <span className="font-mono">{k}</span>
                  <span className={v === 'ok' ? 'text-emerald-600' : 'text-rose-600'}>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
